import { useCallback, useEffect, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { Page } from '~/components/Page'
import { EmptyBlock, ErrorBlock, Panel, SectionHeading, Spinner } from '~/components/ui'
import { useDebouncedValue } from '~/utils/hooks/useDebouncedValue'
import { fetchInfo, fetchListings, ListingSort, TradeInfo, TradeListing } from '../api/trade'
import { BuyDialog } from '../components/BuyDialog'
import { ListingCard } from '../components/ListingCard'
import { MyListings } from '../components/MyListings'
import { OrderHistory } from '../components/OrderHistory'
import { SellPanel } from '../components/SellPanel'

type Tab = 'browse' | 'sell' | 'mine' | 'history'

const TABS: { key: Tab; label: string }[] = [
    { key: 'browse', label: 'Browse' },
    { key: 'sell', label: 'Sell' },
    { key: 'mine', label: 'My listings' },
    { key: 'history', label: 'History' },
]

const TYPES = [
    { value: '', label: 'All' },
    { value: 'ape', label: 'Apes' },
    { value: 'droid', label: 'Droids' },
    { value: 'weapon', label: 'Weapons' },
    { value: 'lootbox', label: 'Boxes' },
]

const SORTS: { value: ListingSort; label: string }[] = [
    { value: 'newest', label: 'Newest' },
    { value: 'price', label: 'Price: low to high' },
    { value: '-price', label: 'Price: high to low' },
]

export const TradePage = () => {
    const [tab, setTab] = useState<Tab>('browse')
    const [info, setInfo] = useState<TradeInfo | null>(null)
    const [listings, setListings] = useState<TradeListing[] | null>(null)
    const [page, setPage] = useState(1)
    const [hasNext, setHasNext] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [loadError, setLoadError] = useState(false)
    const [type, setType] = useState('')
    const [sort, setSort] = useState<ListingSort>('newest')
    const [searchInput, setSearchInput] = useState('')
    // Debounced so typing does not fire a request per keystroke.
    const search = useDebouncedValue(searchInput, 350)
    const [buying, setBuying] = useState<TradeListing | null>(null)
    const [message, setMessage] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)

    // Filters can change while a request is in flight. Only the newest request
    // may write, or a slow earlier page lands on top of the current results.
    const latest = useRef(0)

    useEffect(() => {
        fetchInfo().then(setInfo).catch(() => undefined)
    }, [])

    const loadListings = useCallback(
        (nextPage = 1, append = false) => {
            const ticket = ++latest.current
            setLoadError(false)
            if (append) setLoadingMore(true)
            else setListings(null)

            fetchListings({
                type: type || undefined,
                search: search || undefined,
                sort,
                page: nextPage,
            })
                .then((data) => {
                    if (ticket !== latest.current) return
                    setListings((prev) => (append && prev ? [...prev, ...data.results] : data.results))
                    setPage(data.page)
                    setHasNext(data.has_next)
                })
                .catch(() => {
                    if (ticket === latest.current) setLoadError(true)
                })
                .finally(() => {
                    if (ticket === latest.current) setLoadingMore(false)
                })
        },
        [type, search, sort]
    )

    useEffect(() => {
        if (tab === 'browse') loadListings()
    }, [tab, loadListings])

    const openBuy = (listing: TradeListing) => {
        setError(null)
        setMessage(null)
        setBuying(listing)
    }

    return (
        <Page title='Marketplace' header='Marketplace'>
            <div className='mx-auto mb-16 flex w-full max-w-[1500px] flex-col gap-5 px-4 pt-5 md:mb-24 md:gap-6 md:pt-6'>
                {info && !info.escrow_available && (
                    <Panel className='p-4 text-sm text-[#FFC53D]' border='rgba(255,197,61,0.35)'>
                        The marketplace is not accepting new listings at the moment. Existing
                        listings are unaffected.
                    </Panel>
                )}

                <div className='flex flex-wrap items-center gap-2'>
                    {TABS.map((entry) => (
                        <button
                            key={entry.key}
                            onClick={() => {
                                setTab(entry.key)
                                setError(null)
                                setMessage(null)
                            }}
                            className={twMerge(
                                'border-b-2 px-4 py-2 text-xs font-black uppercase tracking-[0.2em] transition-colors',
                                tab === entry.key
                                    ? 'border-[#03CEFF] text-white'
                                    : 'border-transparent text-white/40 hover:text-white/70'
                            )}
                        >
                            {entry.label}
                        </button>
                    ))}
                    {info && (
                        <span className='ml-auto text-[11px] uppercase tracking-widest text-white/40'>
                            {info.fee_percent}% fee
                        </span>
                    )}
                </div>

                {message && <p className='text-xs text-[#00FF90]'>{message}</p>}
                {error && <p className='text-xs text-[#FF6B6B]'>{error}</p>}

                {tab === 'browse' && (
                    <>
                        <div className='flex flex-wrap items-center gap-2'>
                            {TYPES.map((entry) => (
                                <button
                                    key={entry.value}
                                    onClick={() => setType(entry.value)}
                                    className={twMerge(
                                        'border px-3 py-1.5 text-[11px] font-black uppercase tracking-widest transition-colors',
                                        type === entry.value
                                            ? 'border-[#03CEFF] text-white'
                                            : 'border-white/10 text-white/50 hover:border-white/25'
                                    )}
                                >
                                    {entry.label}
                                </button>
                            ))}
                            <select
                                value={sort}
                                onChange={(e) => setSort(e.target.value as ListingSort)}
                                aria-label='Sort listings'
                                className='ml-auto border border-white/15 bg-black px-3 py-1.5 text-sm text-white outline-none focus:border-[#03CEFF]'
                            >
                                {SORTS.map((entry) => (
                                    <option key={entry.value} value={entry.value}>
                                        {entry.label}
                                    </option>
                                ))}
                            </select>
                            <input
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder='Search by name or mint'
                                className='w-full max-w-xs border border-white/15 bg-black/40 px-3 py-1.5 text-sm text-white outline-none focus:border-[#03CEFF]'
                            />
                        </div>

                        {loadError && <ErrorBlock onRetry={() => loadListings()} />}
                        {!loadError && listings === null && (
                            <div className='flex justify-center py-16'>
                                <Spinner visible={true} />
                            </div>
                        )}
                        {!loadError && listings?.length === 0 && (
                            <EmptyBlock title='Nothing listed yet'>
                                When someone lists an NFT it will show up here.
                            </EmptyBlock>
                        )}
                        {!loadError && listings && listings.length > 0 && (
                            <>
                                <div className='grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5'>
                                    {listings.map((listing) => (
                                        <ListingCard
                                            key={listing.id}
                                            listing={listing}
                                            onBuy={listing.is_mine ? undefined : openBuy}
                                        />
                                    ))}
                                </div>
                                {hasNext && (
                                    <button
                                        onClick={() => loadListings(page + 1, true)}
                                        disabled={loadingMore}
                                        className='min-h-[2.75rem] self-center border border-white/25 px-8 text-xs font-black uppercase tracking-[0.2em] text-white hover:border-white/50 disabled:opacity-40'
                                    >
                                        {loadingMore ? 'Loading...' : 'Load more'}
                                    </button>
                                )}
                            </>
                        )}
                    </>
                )}

                {tab === 'sell' && (
                    <>
                        <SectionHeading title='Sell an NFT' hint='Paid into your BR1 balance' />
                        <SellPanel info={info} onListed={() => setTab('mine')} />
                    </>
                )}

                {tab === 'mine' && (
                    <>
                        <SectionHeading title='My listings' />
                        <MyListings onChanged={() => loadListings()} />
                    </>
                )}

                {tab === 'history' && (
                    <>
                        <SectionHeading title='History' hint='What you bought and sold' />
                        <OrderHistory />
                    </>
                )}

                {buying && (
                    <BuyDialog
                        // Keyed so each opening mounts afresh and draws its own
                        // idempotency key.
                        key={buying.id}
                        listing={buying}
                        onClose={() => setBuying(null)}
                        onBought={() => {
                            setBuying(null)
                            setMessage('Bought. The NFT is on its way to your wallet. Follow it under History.')
                            loadListings()
                        }}
                        // Refresh behind the dialog. It stays open with its
                        // message; the buyer closes it and sees the new price.
                        onPriceChanged={() => loadListings()}
                    />
                )}
            </div>
        </Page>
    )
}
